import { Formik } from "formik";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
  Button,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import SubmitButton from "components/Button/SubmitButton";
import { getFormattedDate, subtractFromDate } from "utils/date";
import { userTransmissionProgressReportSchema } from "schemas/userTransmissionProgressReportSchema";
import useUserTransmissionProgressReportForm from "../hooks/useUserTransmissionProgressReportForm";
import Loading from "components/common/loading/Loading";

const UserTransmissionProgressReportForm = ({ onClose }) => {
  const { users, downloadUserTransmissionProgressReport, isDownloading } =
    useUserTransmissionProgressReportForm({ onClose });

  const handleFormSubmit = async (values) => {
    await downloadUserTransmissionProgressReport(values);
  };

  return (
    <Formik
      initialValues={{
        users: [],
        startDate: getFormattedDate(subtractFromDate(new Date(), 30).toDate()),
        endDate: getFormattedDate(new Date()),
      }}
      validationSchema={userTransmissionProgressReportSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        handleChange,
        handleSubmit,
        errors,
        touched,
      }) => {
        return (
          <>
            {isDownloading ? (
              <Loading />
            ) : (
              <div>
                <div style={{ marginBottom: "20px", marginTop: "20px" }}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    error={touched.users && Boolean(errors.users)}
                  >
                    <InputLabel id="issue-label">Select Users</InputLabel>
                    <Select
                      id="progress_report_users"
                      multiple
                      value={values.users}
                      onChange={handleChange("users")}
                      renderValue={(selected) =>
                        selected
                          ?.map(
                            (selected) =>
                              users.find((user) => user._id === selected)?.name
                          )
                          .join(", ")
                      }
                      label="Select Users"
                    >
                      {[...users]
                        ?.sort((a, b) => a.name.localeCompare(b.name))
                        ?.map((user) => (
                          <MenuItem key={user._id} value={user._id}>
                            <Checkbox
                              checked={values.users.includes(user._id)}
                            />
                            <ListItemText primary={user.name} />
                          </MenuItem>
                        ))}
                    </Select>
                    {touched.users && Boolean(errors.users) && (
                      <FormHelperText error>{errors.users}</FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                  }}
                >
                  <FormControl
                    fullWidth
                    error={touched.startDate && Boolean(errors.startDate)}
                  >
                    <DatePicker
                      margin="none"
                      id="progress_report_start_date"
                      label="Start Date"
                      className="view-pdf__input"
                      name="startDate"
                      value={values.startDate ? dayjs(values.startDate) : null}
                      onChange={(date) => {
                        const value = date ? getFormattedDate(date) : null;
                        setFieldValue("startDate", value);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: touched.startDate && Boolean(errors.startDate),
                          helperText: touched.startDate && errors.startDate,
                          readOnly: true,
                        },
                      }}
                      minDate={dayjs(
                        subtractFromDate(new Date(), 180).toDate()
                      )}
                      maxDate={dayjs(new Date())}
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <DatePicker
                      margin="none"
                      id="progress_report_end_date"
                      label="End Date"
                      className="view-pdf__input"
                      value={values.endDate ? dayjs(values.endDate) : null}
                      onChange={(date) => {
                        const value = date ? getFormattedDate(date) : null;
                        setFieldValue("endDate", value);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: touched.endDate && Boolean(errors.endDate),
                          helperText: touched.endDate && errors.endDate,
                          readOnly: true,
                        },
                      }}
                      minDate={dayjs(
                        subtractFromDate(new Date(), 180).toDate()
                      )}
                      maxDate={dayjs(new Date())}
                    />
                  </FormControl>
                </div>
              </div>
            )}

            <div className="view-pdf__button-container">
              <Button
                onClick={onClose}
                className="view-pdf__button"
                variant="outlined"
                color="secondary"
                disabled={isDownloading}
              >
                Cancel
              </Button>
              <SubmitButton
                buttonText="Download"
                className="view-pdf__button"
                handleClick={handleSubmit}
                disabled={isDownloading}
              />
            </div>
          </>
        );
      }}
    </Formik>
  );
};

export default UserTransmissionProgressReportForm;
